import type {
  CartMode,
  FormConfig,
  QuantityOfferConfig,
  ShippingRate,
  StoreSettings,
  ThankYouConfig,
} from '@cod/config-schema';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  getAnalyticsSummary,
  getConfig,
  getSettings,
  putConfig,
  putSettings,
  type AnalyticsSummary,
} from '../api/client.js';

type Status = 'loading' | 'ready' | 'error';

interface AppStateValue {
  status: Status;
  config: FormConfig | null;
  settings: StoreSettings | null;
  analytics: AnalyticsSummary | null;
  /** Update the in-memory config (used by the designer; not persisted until saveConfig). */
  setConfig: (updater: (prev: FormConfig) => FormConfig) => void;
  /** Persist the config. Pass an explicit config to save it directly (avoids a stale
   *  read of state right after setConfig); omit to save the current in-memory config. */
  saveConfig: (next?: FormConfig) => Promise<boolean>;
  /** Persist ONLY the quantity-offers slice. Re-reads the latest server config and merges the
   *  offers onto it, so an offer save can't clobber unrelated config changes (e.g. Form
   *  Designer / Settings) that were persisted since this page was loaded. */
  saveQuantityOffers: (offers: QuantityOfferConfig[]) => Promise<boolean>;
  /** Persist ONLY the shipping-rates slice (read-modify-write, like saveQuantityOffers). */
  saveShippingRates: (rates: ShippingRate[]) => Promise<boolean>;
  /** Persist ONLY the thank-you slice (read-modify-write, like saveQuantityOffers). */
  saveThankYou: (thankYou: ThankYouConfig) => Promise<boolean>;
  /** Persist ONLY the cart_mode slice (read-modify-write, like saveThankYou). */
  saveCartMode: (mode: CartMode) => Promise<boolean>;
  saveSettings: (patch: Partial<StoreSettings>) => Promise<boolean>;
  refreshAnalytics: () => Promise<void>;
  /** Retry the initial load after an error. */
  reload: () => void;
}

const AppStateContext = createContext<AppStateValue | null>(null);

export function AppStateProvider({ children }: { children: ReactNode }): JSX.Element {
  const { i18n } = useTranslation();
  const [status, setStatus] = useState<Status>('loading');
  const [config, setConfigState] = useState<FormConfig | null>(null);
  const [settings, setSettings] = useState<StoreSettings | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsSummary | null>(null);
  /** Bumped by `reload()` to re-run the initial fetch — the retry affordance the error state needs. */
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    void (async () => {
      try {
        const [cfg, st, an] = await Promise.all([
          getConfig(),
          getSettings(),
          getAnalyticsSummary(),
        ]);
        if (cancelled) return;
        setConfigState(cfg);
        setSettings(st);
        setAnalytics(an);
        // Sync the admin UI language to the stored merchant preference.
        void i18n.changeLanguage(st.admin_language);
        setStatus('ready');
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [i18n, attempt]);

  /**
   * Re-run the initial load. Without this the error branch is a dead end: the merchant's only
   * recourse is reloading the iframe, which inside the Nuvemshop admin is not an obvious action.
   */
  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  const setConfig = useCallback((updater: (prev: FormConfig) => FormConfig) => {
    setConfigState((prev) => (prev ? updater(prev) : prev));
  }, []);

  const saveConfig = useCallback(
    async (next?: FormConfig) => {
      const target = next ?? config;
      if (!target) return false;
      try {
        const saved = await putConfig(target);
        setConfigState(saved);
        return true;
      } catch {
        return false;
      }
    },
    [config],
  );

  const saveQuantityOffers = useCallback(async (offers: QuantityOfferConfig[]) => {
    try {
      // Read-modify-write scoped to the offers slice: fetch the freshest server config and
      // overlay only `_future.quantity_offers`, so a concurrent Form Designer / Settings save
      // (which writes the same JSONB document) isn't overwritten by a stale snapshot.
      const latest = await getConfig();
      const merged: FormConfig = {
        ...latest,
        _future: { ...latest._future, quantity_offers: offers },
      };
      const saved = await putConfig(merged);
      setConfigState(saved);
      return true;
    } catch {
      return false;
    }
  }, []);

  const saveShippingRates = useCallback(async (rates: ShippingRate[]) => {
    try {
      // Read-modify-write scoped to the shipping_rates slice (see saveQuantityOffers).
      const latest = await getConfig();
      const saved = await putConfig({ ...latest, shipping_rates: rates });
      setConfigState(saved);
      return true;
    } catch {
      return false;
    }
  }, []);

  const saveThankYou = useCallback(async (thankYou: ThankYouConfig) => {
    try {
      // Read-modify-write scoped to the thank_you slice (see saveQuantityOffers): overlay only
      // this key on the freshest server config so a concurrent designer save isn't clobbered.
      const latest = await getConfig();
      const saved = await putConfig({ ...latest, thank_you: thankYou });
      setConfigState(saved);
      return true;
    } catch {
      return false;
    }
  }, []);

  const saveCartMode = useCallback(async (mode: CartMode) => {
    try {
      // Read-modify-write scoped to the cart_mode slice (see saveThankYou).
      const latest = await getConfig();
      const saved = await putConfig({ ...latest, cart_mode: mode });
      setConfigState(saved);
      return true;
    } catch {
      return false;
    }
  }, []);

  const saveSettings = useCallback(async (patch: Partial<StoreSettings>) => {
    try {
      const saved = await putSettings(patch);
      setSettings(saved);
      if (patch.admin_language) void i18n.changeLanguage(saved.admin_language);
      return true;
    } catch {
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshAnalytics = useCallback(async () => {
    try {
      setAnalytics(await getAnalyticsSummary());
    } catch {
      /* non-critical */
    }
  }, []);

  const value = useMemo<AppStateValue>(
    () => ({
      status,
      config,
      settings,
      analytics,
      setConfig,
      saveConfig,
      saveQuantityOffers,
      saveShippingRates,
      saveThankYou,
      saveCartMode,
      saveSettings,
      refreshAnalytics,
      reload,
    }),
    [
      status,
      config,
      settings,
      analytics,
      setConfig,
      saveConfig,
      saveQuantityOffers,
      saveShippingRates,
      saveThankYou,
      saveCartMode,
      saveSettings,
      refreshAnalytics,
      reload,
    ],
  );

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
}

export function useAppState(): AppStateValue {
  const ctx = useContext(AppStateContext);
  if (!ctx) throw new Error('useAppState must be used within AppStateProvider');
  return ctx;
}
